/**
 * Доставка новой версии в уже установленное приложение.
 *
 * Приложение с домашнего экрана на iPhone живёт неделями: его не закрывают,
 * а сворачивают. Страница в нём загружена один раз и сама себя не
 * перезапрашивает — человек продолжает пользоваться той сборкой, с которой
 * открыл приложение в первый раз.
 *
 * Так и вышло 05.08.2026: починка поля «Сколько» уехала на сервер, в бандле
 * на zagadar.ru она была, а Захар с телефона писал «всё так же не могу
 * стереть единичку» — и был прав, у него работала прежняя сборка. На следующий
 * день то же самое повторилось с отступом под часы и с листанием.
 *
 * ПОЧЕМУ ПЕРВАЯ ПОПЫТКА НИЧЕГО НЕ ДАЛА. Она была написана на служебном
 * работнике: спрашивала `navigator.serviceWorker.getRegistration()` и ждала
 * события `controllerchange`. А служебного работника в этом приложении нет
 * вовсе — ни файла, ни регистрации. `getRegistration()` возвращал пустоту,
 * `controllerchange` не наступал никогда, и весь код был холостым. Проверки
 * при этом зеленели: они подставляли работника вручную и проверяли выдуманный
 * мир. Урок записан здесь, чтобы следующая правка начиналась с вопроса
 * «а есть ли то, на чём я стою».
 *
 * КАК ЭТО РАБОТАЕТ ТЕПЕРЬ. Сервер и так объявляет, что именно у него работает:
 * `/api/health` отдаёт отпечаток выкладки, и на него же опирается проверка
 * самой выкладки. Приложение запоминает отпечаток, с которым открылось, и
 * сверяется с сервером — при возврате в приложение и раз в пять минут.
 * Отпечаток сменился, значит на сервере новая сборка: перезагружаемся.
 * Страница отдаётся с `cache-control: no-cache`, поэтому перезагрузка приносит
 * свежую разметку и свежие файлы, а не то же самое из памяти браузера.
 *
 * Человеку от этого не должно быть больно, поэтому перезагрузка ждёт удобного
 * момента: пока открыто окно или человек что-то печатает, мы не трогаем
 * страницу и проверим снова позже. Потерять наполовину заполненную карточку
 * ради того, чтобы обновиться на минуту раньше, — плохая мена.
 */

/** Раз в пять минут при открытом приложении — этого хватает и не тревожит сеть. */
const ПЕРИОД_ПРОВЕРКИ_МС = 5 * 60 * 1000;

/** Сколько ждём ответа: обновление не то дело, ради которого стоит висеть. */
const СРОК_ОТВЕТА_МС = 5000;

interface Здоровье {
  build?: unknown;
}

/**
 * Занят ли человек прямо сейчас.
 *
 * Три признака, и любого достаточно: открытое окно, курсор в поле ввода,
 * непустое поле где-нибудь на странице. Последний нужен потому, что человек
 * мог набрать половину названия и уйти отвечать на сообщение — курсора в поле
 * уже нет, а труд его никуда не делся.
 */
function человекЗанят(): boolean {
  if (document.querySelector('[role="dialog"], dialog[open]')) return true;

  const вФокусе = document.activeElement;
  if (вФокусе instanceof HTMLInputElement || вФокусе instanceof HTMLTextAreaElement) return true;
  if (вФокусе instanceof HTMLElement && вФокусе.isContentEditable) return true;

  for (const поле of Array.from(document.querySelectorAll('input, textarea'))) {
    if (поле instanceof HTMLInputElement) {
      // Галочки и переключатели набранным трудом не считаются: их не жалко.
      if (поле.type === 'checkbox' || поле.type === 'radio' || поле.type === 'hidden') continue;
      if (поле.value.trim().length > 0) return true;
    } else if (поле instanceof HTMLTextAreaElement && поле.value.trim().length > 0) {
      return true;
    }
  }

  return false;
}

async function спроситьОтпечаток(): Promise<string | null> {
  try {
    const ответ = await fetch('/api/health', {
      // Спрашиваем сервер, а не память браузера: иначе весь смысл теряется.
      cache: 'no-store',
      credentials: 'same-origin',
      signal: AbortSignal.timeout(СРОК_ОТВЕТА_МС),
    });
    if (!ответ.ok) return null;
    const тело = (await ответ.json()) as Здоровье;
    return typeof тело.build === 'string' && тело.build.length > 0 ? тело.build : null;
  } catch {
    /*
     * Сеть отвалилась или сервер перекладывают. Молчим: это не повод ни
     * перезагружаться, ни жаловаться человеку. Спросим через пять минут.
     */
    return null;
  }
}

export function следитьЗаНовойВерсией(): void {
  if (typeof fetch !== 'function' || typeof document === 'undefined') return;

  let свой: string | null = null;
  let ужеПерезагружаемся = false;

  const проверить = async (): Promise<void> => {
    if (ужеПерезагружаемся) return;

    const наСервере = await спроситьОтпечаток();
    if (!наСервере) return;

    /*
     * Первый ответ задаёт точку отсчёта, а не повод перезагружаться.
     * Иначе первое же открытие приложения уходило бы в петлю перезагрузок.
     */
    if (свой === null) {
      свой = наСервере;
      return;
    }

    if (наСервере === свой) return;
    if (человекЗанят()) return;

    ужеПерезагружаемся = true;
    window.location.reload();
  };

  void проверить();

  /*
   * Возврат в приложение — самый частый момент, когда человек готов подождать
   * долю секунды. Таймер оставлен на случай, когда приложение держат открытым
   * подолгу.
   */
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void проверить();
  });
  window.setInterval(() => void проверить(), ПЕРИОД_ПРОВЕРКИ_МС);
}
